import React, { useReducer, createContext, useContext, ReactNode, Dispatch, useCallback, useMemo } from 'react';
import { App } from 'antd';
import globalStore from '@/store/global';

// ==================== 类型定义 ====================

/**
 * 用户权限状态接口
 */
export interface AuthStatus {
    /** 是否已登录 */
    isLoggedIn: boolean;
    /** 是否有权限 */
    hasAuth: boolean;
    /** 无权限提示信息 */
    noAuthTip: string;
    /** 是否已获取预测分 */
    hasPredictionScore: boolean;
    /** 获取预测分的地址 */
    predictionScoreUrl: string;
}

/**
 * 权限检查结果接口
 */
export interface AuthCheckResult {
    /** 是否通过检查 */
    passed: boolean;
    /** 失败原因 */
    reason?: 'NOT_LOGGED_IN' | 'NO_AUTH' | 'NO_PREDICTION_SCORE' | 'UNKNOWN';
    /** 错误信息 */
    message?: string;
}

/**
 * 权限检查选项接口
 */
export interface AuthCheckOptions {
    /** 是否显示错误提示 */
    showError?: boolean;
    /** 是否阻止事件冒泡 */
    preventBubble?: boolean;
    /** 自定义错误处理 */
    onError?: (result: AuthCheckResult) => void;
}

/**
 * 权限动作类型
 */
export type AuthAction =
    | { type: 'SET_AUTH_STATUS'; payload: Partial<AuthStatus> }
    | { type: 'RESET_AUTH_STATUS' }
    | { type: 'UPDATE_LOGIN_STATUS'; payload: { isLoggedIn: boolean } }
    | { type: 'UPDATE_AUTH_STATUS'; payload: { hasAuth: boolean; noAuthTip?: string } }
    | { type: 'UPDATE_PREDICTION_SCORE_STATUS'; payload: { hasPredictionScore: boolean; predictionScoreUrl?: string } };

// ==================== 初始状态 ====================

const initialAuthStatus: AuthStatus = {
    isLoggedIn: false,
    hasAuth: false,
    noAuthTip: '',
    hasPredictionScore: false,
    predictionScoreUrl: '',
};

// ==================== Reducer ====================

/**
 * 权限状态管理reducer
 */
function authReducer(state: AuthStatus, action: AuthAction): AuthStatus {
    switch (action.type) {
        case 'SET_AUTH_STATUS':
            return {
                ...state,
                ...action.payload,
            };
        case 'RESET_AUTH_STATUS':
            return { ...initialAuthStatus };
        case 'UPDATE_LOGIN_STATUS':
            return {
                ...state,
                isLoggedIn: action.payload.isLoggedIn,
            };
        case 'UPDATE_AUTH_STATUS':
            return {
                ...state,
                hasAuth: action.payload.hasAuth,
                noAuthTip: action.payload.noAuthTip || state.noAuthTip,
            };
        case 'UPDATE_PREDICTION_SCORE_STATUS':
            return {
                ...state,
                hasPredictionScore: action.payload.hasPredictionScore,
                predictionScoreUrl: action.payload.predictionScoreUrl || state.predictionScoreUrl,
            };
        default:
            throw new Error(`Unknown action type: ${(action as AuthAction).type}`);
    }
}

// ==================== Context 接口 ====================

/**
 * 认证上下文数据接口
 */
export interface AuthContextData {
    /** 当前权限状态 */
    authStatus: AuthStatus;
    /** 状态分发器 */
    dispatch: Dispatch<AuthAction>;
    /** 权限检查工具 */
    authUtils: {
        /** 检查登录状态 */
        checkLogin: (options?: AuthCheckOptions) => AuthCheckResult;
        /** 检查权限状态 */
        checkAuth: (options?: AuthCheckOptions) => AuthCheckResult;
        /** 检查预测分状态 */
        checkPredictionScore: (options?: AuthCheckOptions) => AuthCheckResult;
        /** 综合权限检查（按顺序：登录 > 权限 > 预测分） */
        checkFullAuth: (options?: AuthCheckOptions) => AuthCheckResult;
        /** 带完整权限检查的事件处理器包装器 */
        withFullAuthCheck: <T extends any[]>(
            handler: (...args: T) => void,
            options?: AuthCheckOptions & {
                /** 总是执行的回调（如埋点） */
                alwaysCall?: (...args: T) => void;
            }
        ) => (...args: T) => void;
        /** 带登录检查的事件处理器包装器 */
        withLoginCheck: <T extends any[]>(handler: (...args: T) => void, options?: AuthCheckOptions) => (...args: T) => void;
    };
    /** 权限状态更新方法 */
    authActions: {
        /** 设置完整权限状态 */
        setAuthStatus: (status: Partial<AuthStatus>) => void;
        /** 更新登录状态 */
        updateLoginStatus: (isLoggedIn: boolean) => void;
        /** 更新权限状态 */
        updateAuthStatus: (hasAuth: boolean, noAuthTip?: string) => void;
        /** 更新预测分状态 */
        updatePredictionScoreStatus: (hasPredictionScore: boolean, predictionScoreUrl?: string) => void;
        /** 重置权限状态 */
        resetAuthStatus: () => void;
    };
}

// ==================== Context 创建 ====================

const AuthContext = createContext<AuthContextData | undefined>(undefined);

// ==================== Hooks ====================

/**
 * 使用认证上下文的Hook
 */
export const useAuthContext = (): AuthContextData => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
};

/**
 * 使用权限检查的Hook
 */
export const useAuthCheck = () => {
    const { authUtils } = useAuthContext();
    return authUtils;
};

/**
 * 使用权限状态的Hook
 */
export const useAuthStatus = () => {
    const { authStatus, authActions } = useAuthContext();
    return { authStatus, authActions };
};

// ==================== Provider 组件 ====================

interface AuthProviderProps {
    children: ReactNode;
}

/**
 * 认证状态提供者组件
 */
const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const { message } = App.useApp();
    const [authStatus, dispatch] = useReducer(authReducer, initialAuthStatus);

    // ==================== 权限检查方法 ====================

    /**
     * 检查登录状态
     */
    const checkLogin = useCallback(
        (options: AuthCheckOptions = {}): AuthCheckResult => {
            const { showError = true, onError } = options;

            if (!authStatus.isLoggedIn) {
                // 触发登录流程
                globalStore.publish('main-app', { type: 'signIn' });

                const result: AuthCheckResult = {
                    passed: false,
                    reason: 'NOT_LOGGED_IN',
                    message: '请先登录',
                };

                if (onError) {
                    onError(result);
                }

                return result;
            }

            return { passed: true };
        },
        [authStatus.isLoggedIn]
    );

    /**
     * 检查权限状态
     */
    const checkAuth = useCallback(
        (options: AuthCheckOptions = {}): AuthCheckResult => {
            const { showError = true, onError } = options;

            if (!authStatus.hasAuth) {
                const result: AuthCheckResult = {
                    passed: false,
                    reason: 'NO_AUTH',
                    message: authStatus.noAuthTip || '暂无权限',
                };

                if (showError && authStatus.noAuthTip) {
                    message.warning(authStatus.noAuthTip);
                }

                if (onError) {
                    onError(result);
                }

                return result;
            }

            return { passed: true };
        },
        [authStatus.hasAuth, authStatus.noAuthTip, message]
    );

    /**
     * 检查预测分状态
     */
    const checkPredictionScore = useCallback(
        (options: AuthCheckOptions = {}): AuthCheckResult => {
            const { showError = true, onError } = options;

            if (!authStatus.hasPredictionScore) {
                const result: AuthCheckResult = {
                    passed: false,
                    reason: 'NO_PREDICTION_SCORE',
                    message: '请先获取预测分',
                };

                if (showError) {
                    if (authStatus.predictionScoreUrl) {
                        // 有URL时跳转到获取预测分页面
                        window.open(authStatus.predictionScoreUrl, '_self');
                    } else {
                        // 没有URL时显示提示
                        message.warning('请先获取预测分');
                    }
                }

                if (onError) {
                    onError(result);
                }

                return result;
            }

            return { passed: true };
        },
        [authStatus.hasPredictionScore, authStatus.predictionScoreUrl, message]
    );

    /**
     * 综合权限检查（按顺序：登录 > 权限 > 预测分）
     */
    const checkFullAuth = useCallback(
        (options: AuthCheckOptions = {}): AuthCheckResult => {
            // 1. 检查登录状态
            const loginResult = checkLogin(options);
            if (!loginResult.passed) {
                return loginResult;
            }

            // 2. 检查权限状态
            const authResult = checkAuth(options);
            if (!authResult.passed) {
                return authResult;
            }

            // 3. 检查预测分状态
            return checkPredictionScore(options);
        },
        [checkLogin, checkAuth, checkPredictionScore]
    );

    /**
     * 带完整权限检查的事件处理器包装器
     */
    const withFullAuthCheck = useCallback(
        <T extends any[]>(handler: (...args: T) => void, options: AuthCheckOptions & { alwaysCall?: (...args: T) => void } = {}) => {
            return (...args: T) => {
                const { preventBubble = true, alwaysCall, ...checkOptions } = options;

                // 阻止事件冒泡
                if (preventBubble && args[0] && typeof args[0].stopPropagation === 'function') {
                    (args[0] as any).stopPropagation();
                }

                // 执行总是调用的回调（如埋点）
                if (alwaysCall) {
                    alwaysCall(...args);
                }

                // 进行完整权限检查
                const result = checkFullAuth(checkOptions);
                if (result.passed) {
                    handler(...args);
                }
            };
        },
        [checkFullAuth]
    );

    /**
     * 带登录检查的事件处理器包装器
     */
    const withLoginCheck = useCallback(
        <T extends any[]>(handler: (...args: T) => void, options: AuthCheckOptions = {}) => {
            return (...args: T) => {
                const { preventBubble = true, ...checkOptions } = options;

                // 阻止事件冒泡
                if (preventBubble && args[0] && typeof args[0].stopPropagation === 'function') {
                    (args[0] as any).stopPropagation();
                }

                // 进行登录检查
                const result = checkLogin(checkOptions);
                if (result.passed) {
                    handler(...args);
                }
            };
        },
        [checkLogin]
    );

    // ==================== 权限状态更新方法 ====================

    const authActions = useMemo(
        () => ({
            setAuthStatus: (status: Partial<AuthStatus>) => {
                dispatch({ type: 'SET_AUTH_STATUS', payload: status });
            },
            updateLoginStatus: (isLoggedIn: boolean) => {
                dispatch({ type: 'UPDATE_LOGIN_STATUS', payload: { isLoggedIn } });
            },
            updateAuthStatus: (hasAuth: boolean, noAuthTip?: string) => {
                dispatch({ type: 'UPDATE_AUTH_STATUS', payload: { hasAuth, noAuthTip } });
            },
            updatePredictionScoreStatus: (hasPredictionScore: boolean, predictionScoreUrl?: string) => {
                dispatch({ type: 'UPDATE_PREDICTION_SCORE_STATUS', payload: { hasPredictionScore, predictionScoreUrl } });
            },
            resetAuthStatus: () => {
                dispatch({ type: 'RESET_AUTH_STATUS' });
            },
        }),
        []
    );

    // ==================== Context 值 ====================

    const contextValue = useMemo<AuthContextData>(
        () => ({
            authStatus,
            dispatch,
            authUtils: {
                checkLogin,
                checkAuth,
                checkPredictionScore,
                checkFullAuth,
                withFullAuthCheck,
                withLoginCheck,
            },
            authActions,
        }),
        [authStatus, checkLogin, checkAuth, checkPredictionScore, checkFullAuth, withFullAuthCheck, withLoginCheck, authActions]
    );

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// ==================== 导出 ====================

export { authReducer, initialAuthStatus, AuthContext };

export default AuthProvider;
